import React, { memo } from 'react';
import { connect } from 'react-redux';
import { NavLink, Route, Routes } from 'react-router';
import { FormattedMessage, IntlProvider } from 'react-intl';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';

import { DefaultUserMenu, EulaConsent } from '@rio-cloud/rio-user-menu-component';
import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';

import { DEFAULT_LOCALE, extractLanguage } from '../../configuration/lang/lang';
import { type DisplayMessages, getLanguageData, getLocale } from '../../configuration/lang/langSlice';
import { DriversContainer } from './drivers/main/containers/DriversContainer';
import IframeResizer from 'iframe-resizer-react';

import { reportErrorToSentry } from '../../configuration/setup/sentry';
import { config } from '../../config';
import { getTenant, isUserSessionExpired } from '../../configuration/login/loginSlice';
import DefaultRedirect from './router/DefaultRedirect';
import { RioNotifications } from '@rio-cloud/rio-notifications-component';
import { getSessionWarningAcknowledgedRtk, hideSessionWarning } from './appSlice';
import type { AppDispatch, RootState } from '../../configuration/setup/store';
import { messagesEN } from '../../test/testUtils';
import { getIdToken } from '../../configuration/tokenHandling/tokenHandlingSlice';
import { useAppSelector } from '../../configuration/setup/hooks.ts';
import { OrderChat } from '@rio-cloud/rio-order-chat-component';

// biome-ignore lint/a11y/useAnchorContent: suppress out of convenience during migration to biome
const HOME_ROUTE = <a href={'https://home.rio.cloud'}>{''}</a>;

interface DriverAdminProps {
    hideSessionWarning: () => void;
    locale: string;
    sessionWarningAcknowledged: boolean;
    userSessionExpired: boolean;
    idToken: string | null;
}

const DriverAdmin = memo((props: DriverAdminProps) => {
    const { hideSessionWarning, locale, sessionWarningAcknowledged, userSessionExpired, idToken } = props;

    const postLogoutRedirectUri = config.login.postLogoutRedirectUri;

    const environment = import.meta.env.PROD ? 'production' : 'local';

    const userMenu = (
        <DefaultUserMenu
            environment={environment}
            postLogout={
                idToken && postLogoutRedirectUri
                    ? { redirectUri: postLogoutRedirectUri, idTokenHint: idToken }
                    : undefined
            }
        />
    );
    const tenant = useAppSelector(getTenant) || 'unknown';
    const chat = <OrderChat tenant={tenant} />;
    const notifications = <RioNotifications />;
    const actionBarItems = config.isCapabilityMonitoring ? [] : [chat, notifications, userMenu];
    const menuUrl = config.backend.MENU_SERVICE;
    const menu = config.isCapabilityMonitoring ? undefined : <IframeResizer className='iFrameResizer' src={menuUrl} />;
    const appTitle = <FormattedMessage id={'intl-msg:moduleName'} />;

    const navItems = [
        {
            key: 'assets',
            route: (
                <a href={config.links.ASSET_ADMINISTRATION_WEB}>
                    <FormattedMessage id={'intl-msg:tabTitle.assets'} />
                </a>
            ),
        },
        {
            key: 'drivers',
            route: (
                <NavLink to={'/drivers'}>
                    <FormattedMessage id={'intl-msg:tabTitle.drivers'} />
                </NavLink>
            ),
        },
        {
            key: 'tags',
            route: (
                <a href={config.links.TAG_ADMINISTRATION_WEB}>
                    <FormattedMessage id={'intl-msg:tabTitle.tags'} />
                </a>
            ),
        },
    ];

    const header = (
        <ApplicationLayout.Header>
            <ApplicationHeader
                actionBarItems={actionBarItems}
                appNavigator={menu}
                homeRoute={HOME_ROUTE}
                label={appTitle}
                navItems={navItems}
            />
            <EulaConsent environment={environment} />
        </ApplicationLayout.Header>
    );

    return (
        <React.Fragment>
            <SessionExpiredDialog
                locale={locale}
                onClose={hideSessionWarning}
                show={userSessionExpired && !sessionWarningAcknowledged}
            />
            <Routes>
                <Route path={'/drivers'} element={<DriversContainer header={header} />} />
                <Route path={'/drivers/:driverId'} element={<DriversContainer header={header} />} />
                <Route path='*' element={<DefaultRedirect />} />
            </Routes>
        </React.Fragment>
    );
});

// biome-ignore lint/suspicious/noExplicitAny: should be OnErrorFn instead of (intlError: any) => void, but type does not really matter for us here
const intlErrorHandler: (intlError: any) => void = intlError => {
    reportErrorToSentry(intlError);
    console.error(intlError);
};

type AppProps = AppStateProps & AppDispatchProps;

interface AppStateProps {
    lang: string | undefined;
    languageData: DisplayMessages;
    locale: string;
    sessionWarningAcknowledged: boolean;
    userSessionExpired: boolean;
    idToken: string | null;
}

interface AppDispatchProps {
    hideSessionWarning: () => void;
}

export const App = (props: AppProps) => {
    const { languageData, locale } = props;
    if (!languageData) {
        return null;
    }
    return (
        <IntlProvider
            defaultLocale={DEFAULT_LOCALE}
            key={locale}
            locale={locale}
            messages={languageData}
            onError={intlErrorHandler}
        >
            <DriverAdmin {...props} />
        </IntlProvider>
    );
};

const mapStateToProps = (state: RootState): AppStateProps => ({
    lang: extractLanguage(getLocale(state)),
    languageData: getLanguageData(state) || messagesEN,
    locale: getLocale(state) || 'en-GB',
    sessionWarningAcknowledged: getSessionWarningAcknowledgedRtk(state),
    userSessionExpired: isUserSessionExpired(state),
    idToken: getIdToken(state),
});

const mapDispatchToProps = (dispatch: AppDispatch): AppDispatchProps => ({
    hideSessionWarning: () => {
        // dispatch(hideSessionWarningAction()); // mse - remove
        dispatch(hideSessionWarning());
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
